import React, { useState } from 'react';
import VisaPaymentPanel from '../shared/components/payments/VisaPaymentPanel';
import { useNavigate } from 'react-router-dom';
import Papa from 'papaparse';
import { downloadInvoiceJSON, downloadInvoiceCSV } from '../lib/invoiceExport';
import InvoicePrintView from '../shared/components/InvoicePrintView';
import { buildInvoiceFromCSV, createEmptyInvoice, recalculateInvoiceTotals, validateInvoice } from '../lib/invoiceBuilder';
import { generateInvoicePDF } from '../lib/pdfGenerator';
import {
  createTransaction,
  completeTransaction,
  reopenTransaction,
  markPendingFulfillment,
  isTransactionEditable,
  addInvoiceToTransaction,
  updateInvoiceInTransaction,
  removeInvoiceFromTransaction,
  getTransactionSummary,
  getTransactionHistory,
  TransactionStatus,
  TransactionEventType
} from '../lib/transactions/transactionModel';

export default function SalesTransactions() {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [expandedTxnId, setExpandedTxnId] = useState(null);

  function handleCreateTransaction() {
    const newTxn = createTransaction({
      customerName: 'New Customer',
      originPlatform: 'Direct'
    });
    setTransactions([newTxn, ...transactions]);
    setExpandedTxnId(newTxn.transactionId);
    console.log('Created transaction:', newTxn);
  }

  function handleToggleComplete(txnId) {
    setTransactions(transactions.map(txn => {
      if (txn.transactionId === txnId) {
        if (txn.status === TransactionStatus.OPEN) {
          return markPendingFulfillment(txn);
        } else if (txn.status === TransactionStatus.PENDING_FULFILLMENT) {
          return completeTransaction(txn);
        } else {
          return reopenTransaction(txn);
        }
      }
      return txn;
    }));
  }

  function handleMarkComplete(txnId) {
    setTransactions(transactions.map(txn => {
      if (txn.transactionId === txnId) {
        return completeTransaction(txn);
      }
      return txn;
    }));
  }

  function handleMarkPending(txnId) {
    setTransactions(transactions.map(txn => {
      if (txn.transactionId === txnId) {
        return markPendingFulfillment(txn);
      }
      return txn;
    }));
  }

  function handleDeleteTransaction(txnId) {
    if (confirm('Delete this transaction and all its invoices?')) {
      setTransactions(transactions.filter(txn => txn.transactionId !== txnId));
    }
  }

  function handleAddInvoice(txnId) {
    setTransactions(transactions.map(txn => {
      if (txn.transactionId === txnId) {
        const newInvoice = createEmptyInvoice({
          transactionId: txn.transactionId,
          customerName: txn.customerName,
          originPlatform: txn.originPlatform
        });
        return addInvoiceToTransaction(txn, newInvoice);
      }
      return txn;
    }));
  }

  function handleUpdateInvoice(txnId, invoiceId, updates) {
    setTransactions(transactions.map(txn => {
      if (txn.transactionId === txnId) {
        return updateInvoiceInTransaction(txn, invoiceId, updates);
      }
      return txn;
    }));
  }

  function handleDeleteInvoice(txnId, invoiceId) {
    if (confirm('Remove this invoice from the transaction?')) {
      setTransactions(transactions.map(txn => {
        if (txn.transactionId === txnId) {
          return removeInvoiceFromTransaction(txn, invoiceId);
        }
        return txn;
      }));
    }
  }

  function handleCSVImport(txnId, file, paymentAttempts = []) {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        try {
          const txn = transactions.find(t => t.transactionId === txnId);
          const invoice = buildInvoiceFromCSV(result.data, txnId, {
            customerName: txn?.customerName,
            originPlatform: txn?.originPlatform
          });

          // Attach any local-only payment attempts passed from the editor
          invoice.paymentAttempts = paymentAttempts || invoice.paymentAttempts || [];
          
          const validation = validateInvoice(invoice);
          if (!validation.valid) {
            alert(`Invoice validation failed:\n${validation.errors.join('\n')}`);
            return;
          }

          setTransactions(transactions.map(t => {
            if (t.transactionId === txnId) {
              return addInvoiceToTransaction(t, invoice);
            }
            return t;
          }));
        } catch (err) {
          alert(`CSV Import Failed: ${err.message}`);
        }
      },
      error: (err) => {
        alert(`CSV Parsing Failed: ${err.message}`);
      }
    });
  }

  const stats = {
    total: transactions.length,
    open: transactions.filter(t => t.status === TransactionStatus.OPEN).length,
    pending: transactions.filter(t => t.status === TransactionStatus.PENDING_FULFILLMENT).length,
    completed: transactions.filter(t => t.status === TransactionStatus.COMPLETED).length
  };

  console.log('Transactions:', transactions);

  return (
    <div className="min-h-screen surface p-6">
      <div className="mb-8">
        <h1 className="text-ice text-3xl font-bold mb-2">
          Sales Transactions
        </h1>
      </div>

      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-ink border border-edge rounded-hard p-4">
          <div className="text-mist/70 text-sm mb-1">Total</div>
          <div className="text-ice text-2xl font-bold">{stats.total}</div>
        </div>
        <div className="bg-ink border border-edge rounded-hard p-4">
          <div className="text-mist/70 text-sm mb-1">Open</div>
          <div className="text-emerald-400 text-2xl font-bold">{stats.open}</div>
        </div>
        <div className="bg-ink border border-edge rounded-hard p-4">
          <div className="text-mist/70 text-sm mb-1">Pending</div>
          <div className="text-yellow-400 text-2xl font-bold">{stats.pending}</div>
        </div>
        <div className="bg-ink border border-edge rounded-hard p-4">
          <div className="text-mist/70 text-sm mb-1">Completed</div>
          <div className="text-blue-400 text-2xl font-bold">{stats.completed}</div>
        </div>
      </div>

      <button
        onClick={handleCreateTransaction}
        className="bg-brand text-ink px-6 py-2 rounded-none font-semibold mb-6"
      >
        + New Transaction
      </button>

      {transactions.length === 0 ? (
        <div className="border border-edge rounded-hard bg-ink p-12 text-center">
          <div className="text-mist text-lg mb-2">No transactions yet</div>
          <div className="text-mist/70 mb-6">
            Create a transaction to get started
          </div>
          <button
            onClick={handleCreateTransaction}
            className="chip px-6 py-2 rounded-none"
          >
            + New Transaction
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {transactions.map(txn => (
            <TransactionCard
              key={txn.transactionId}
              transaction={txn}
              expanded={expandedTxnId === txn.transactionId}
              onToggleExpand={() => setExpandedTxnId(
                expandedTxnId === txn.transactionId ? null : txn.transactionId
              )}
              onToggleComplete={() => handleToggleComplete(txn.transactionId)}
              onMarkPending={() => handleMarkPending(txn.transactionId)}
              onMarkComplete={() => handleMarkComplete(txn.transactionId)}
              onDelete={() => handleDeleteTransaction(txn.transactionId)}
              onAddInvoice={() => handleAddInvoice(txn.transactionId)}
              onUpdateInvoice={(invoiceId, updates) => handleUpdateInvoice(txn.transactionId, invoiceId, updates)}
              onDeleteInvoice={(invoiceId) => handleDeleteInvoice(txn.transactionId, invoiceId)}
              onCSVImport={(file) => handleCSVImport(txn.transactionId, file)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function TransactionCard({ 
  transaction, 
  expanded, 
  onToggleExpand, 
  onToggleComplete,
  onMarkPending,
  onMarkComplete,
  onDelete,
  onAddInvoice,
  onUpdateInvoice,
  onDeleteInvoice,
  onCSVImport
}) {
  const summary = getTransactionSummary(transaction);
  const [showCSVModal, setShowCSVModal] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    customerName: '',
    shippingAddress: '',
    lineItems: [
      { description: '', quantity: 1, unitPrice: 0, lineTotal: 0 },
      { description: '', quantity: 1, unitPrice: 0, lineTotal: 0 },
      { description: '', quantity: 1, unitPrice: 0, lineTotal: 0 }
    ],
    fees: [
      { description: '', amount: 0 },
      { description: '', amount: 0 },
      { description: '', amount: 0 }
    ],
    payments: [
      { date: '', source: '', description: '', amount: 0 },
      { date: '', source: '', description: '', amount: 0 },
      { date: '', source: '', description: '', amount: 0 },
      { date: '', source: '', description: '', amount: 0 }
    ],
    // Local simulated payment attempts (VISA only) attached to this draft invoice
    paymentAttempts: []
  });
  const navigate = useNavigate();

  function handleCSVUpload(e) {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const csvText = event.target.result;
        
        Papa.parse(csvText, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => {
            const rows = results.data.filter(
              r => r && Object.keys(r).length > 0
            );
            
            const newFormData = {
              customerName: '',
              shippingAddress: '',
              lineItems: [],
              fees: [],
              payments: [],
            };
            
            rows.forEach((row) => {
              const recordType = (row.record_type || row.section || '').toLowerCase().trim();
              const field = row.field || row.description || '';
              const value = row.value || row.amount || '';
              
              // HEADER/CUSTOMER INFO
              if (recordType === 'header') {
                if (field === 'customer_name') {
                  newFormData.customerName = value;
                } else if (field === 'shipping_address') {
                  newFormData.shippingAddress = value;
                }
              }
              
              // OLD FORMAT: CUSTOMER
              if (recordType === 'customer') {
                let customerName = field.trim();
                customerName = customerName.replace(/^Bill To:\s*/i, '');
                newFormData.customerName = customerName;
              }
              
              // OLD FORMAT: SHIPPING ADDRESS
              if (recordType === 'shipping_address' || recordType === 'ship_to') {
                let shippingAddress = field.trim();
                shippingAddress = shippingAddress.replace(/^Ship To:\s*/i, '');
                newFormData.shippingAddress = shippingAddress;
              }
              
              // PRODUCTS
              if (recordType === 'product' || recordType === 'sale' || recordType === 'upgrade') {
                newFormData.lineItems.push({
                  description: field || '',
                  quantity: 1,
                  unitPrice: Number(value) || 0,
                  lineTotal: Number(value) || 0,
                });
              }
              
              // FEES & SHIPPING
              if (recordType === 'fee' || recordType === 'fees' || recordType === 'shipping') {
                newFormData.fees.push({
                  description: field || '',
                  amount: Number(value) || 0,
                });
              }
              
              // PAYMENT HISTORY
              if (recordType === 'payment') {
                newFormData.payments.push({
                  date: '',
                  source: '',
                  description: field || '',
                  amount: Number(value) || 0,
                });
              }
            });
            
            // Ensure minimum rows
            while (newFormData.lineItems.length < 3) {
              newFormData.lineItems.push({ description: '', quantity: 1, unitPrice: 0, lineTotal: 0 });
            }
            while (newFormData.fees.length < 3) {
              newFormData.fees.push({ description: '', amount: 0 });
            }
            while (newFormData.payments.length < 4) {
              newFormData.payments.push({ date: '', source: '', description: '', amount: 0 });
            }
            
            setFormData(prev => ({
              ...prev,
              ...newFormData,
            }));
          },
          error: (error) => {
            alert('Error parsing CSV: ' + error.message);
          }
        });
      };
      reader.readAsText(file);
      e.target.value = '';
    }
  }

  function handleSaveInvoice() {
    // Convert form data to CSV format
    const csvRows = [
      'record_type,section,description,qty,unit_price,amount,currency,visible_to_customer,affects_invoice_total,reference,date,notes'
    ];
    
    // Add customer
    csvRows.push(`meta,customer,${formData.customerName},,,,USD,true,false,,${new Date().toISOString().split('T')[0]},`);
    
    // Add shipping address
    if (formData.shippingAddress) {
      csvRows.push(`meta,shipping_address,${formData.shippingAddress},,,,USD,true,false,,${new Date().toISOString().split('T')[0]},`);
    }
    
    // Add line items
    formData.lineItems.forEach(item => {
      if (item.description) {
        csvRows.push(`line_item,product,${item.description},${item.quantity},${item.unitPrice},${item.lineTotal},USD,true,true,,${new Date().toISOString().split('T')[0]},`);
      }
    });
    
    // Add fees
    formData.fees.forEach(fee => {
      if (fee.description) {
        csvRows.push(`fee,platform,${fee.description},,,${fee.amount},USD,true,false,,${new Date().toISOString().split('T')[0]},`);
      }
    });
    
    // Add payments
    formData.payments.forEach(payment => {
      if (payment.description) {
        csvRows.push(`payment,payment,${payment.description},,,${payment.amount},USD,true,false,${payment.source},${payment.date},`);
      }
    });
    
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const file = new File([blob], 'invoice.csv', { type: 'text/csv' });
    // Pass along local paymentAttempts so they are persisted into the created invoice
    onCSVImport(file, formData.paymentAttempts || []);
    setShowCSVModal(false);
  }

  return (
    <div className="border border-edge rounded-hard bg-[#0b171d] overflow-hidden">
      {/* Transaction Header */}
      <div 
        className="p-6 cursor-pointer hover:bg-[#0e1d26] transition-colors"
        onClick={onToggleExpand}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h3 className="text-ice text-xl font-semibold">
                {new Date(transaction.createdDate).toLocaleDateString('en-US', { 
                  month: 'short', 
                  day: '2-digit', 
                  year: '2-digit' 
                }).replace(',', '')} {transaction.customerName}
              </h3>
              <span className={`text-xs px-2 py-1 rounded ${
                transaction.status === TransactionStatus.OPEN
                  ? 'text-emerald-400 bg-emerald-400/10'
                  : transaction.status === TransactionStatus.PENDING_FULFILLMENT
                  ? 'text-yellow-400 bg-yellow-400/10'
                  : 'text-blue-400 bg-blue-400/10'
              }`}>
                {transaction.status}
              </span>
            </div>
            <div className="text-sm text-mist/70">
              {transaction.originPlatform}
            </div>
          </div>
          <div className="text-right ml-6">
            <div className="text-mist/70 text-sm mb-1">Total Amount</div>
            <div className="text-ice text-2xl font-bold">
              ${summary.totalAmount.toFixed(2)}
            </div>
            <div className="text-mist/70 text-xs mt-1">
              {summary.totalInvoices} invoice{summary.totalInvoices !== 1 ? 's' : ''}
            </div>
          </div>
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="bg-ink/50 p-6 space-y-6">
          <div>
            <h4 className="text-ice font-semibold mb-3 text-lg border-b border-edge/50 pb-2">
              Invoices ({transaction.invoices.length})
            </h4>
            {transaction.invoices.length === 0 ? (
              <div className="border border-edge/50 rounded-none bg-[#0b171d] p-8 text-center">
                <div className="text-mist text-sm mb-2">No invoices yet</div>
                <div className="text-mist/70 text-xs mb-4">
                  {summary.isEditable 
                    ? 'Add an invoice to this transaction'
                    : 'Transaction is completed (no invoices can be added)'}
                </div>
                {summary.isEditable && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowCSVModal(true); }}
                    className="bg-brand/10 border border-brand/30 text-white hover:bg-brand/20 px-4 py-2 rounded-none text-sm font-medium"
                  >
                    Create / Edit Invoice
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {transaction.invoices.map(invoice => (
                  <div key={invoice.invoice_id} className="border border-edge/50 rounded-none bg-[#0e1d26] p-4">
                    <div className="text-ice font-semibold">{invoice.invoice_id}</div>
                    <div className="text-mist/70 text-sm">{invoice.customer}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="border-t border-edge/50 pt-4">
            <div className="flex gap-2 justify-end">
              <button
                onClick={(e) => { 
                  e.stopPropagation(); 
                  // TODO: Implement PDF download
                  console.log('Download invoice PDF');
                }}
                className="bg-brand/10 border border-brand/30 text-white hover:bg-brand/20 px-4 py-2 rounded-none text-sm font-medium"
              >
                📥 Download
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setShowCSVModal(true); }}
                className="bg-brand text-ink px-4 py-2 rounded-none text-sm font-medium"
              >
                Billing
              </button>
              
              <button
                onClick={(e) => { 
                  e.stopPropagation(); 
                  if (confirm('Delete this transaction?')) onDelete(); 
                }}
                className="border border-red-400 text-red-400 hover:bg-red-400/10 px-4 py-2 rounded-none text-sm font-medium"
              >
                🗑️ Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Invoice Modal */}
      {showCSVModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-6" onClick={() => setShowCSVModal(false)}>
          <div 
            className="bg-[#0b171d] border border-brand rounded-none max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-edge/50 bg-ink/30">
              <div className="flex items-center justify-between p-6">
                <div>
                  <h2 className="text-ice text-2xl font-bold">Edit Invoice</h2>
                  <p className="text-mist/70 text-sm mt-1">Customer-facing document</p>
                </div>
                <button onClick={() => setShowCSVModal(false)} className="text-mist/70 hover:text-ice text-2xl leading-none">×</button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6 bg-[#0b171d] space-y-6">
              <div className="border border-edge/50 rounded-none p-4 bg-ink/20">
                <h3 className="text-brand font-bold text-sm mb-3 uppercase tracking-wide">Bill To</h3>
                <input type="text" value={formData.customerName} onChange={(e) => setFormData({...formData, customerName: e.target.value})} className="w-full bg-ink/50 border border-edge/50 text-ice px-3 py-2 text-sm rounded-none" placeholder="Enter customer name" />
              </div>

              <div className="border border-edge/50 rounded-none p-4 bg-ink/20">
                <h3 className="text-brand font-bold text-sm mb-3 uppercase tracking-wide">Ship To</h3>
                <textarea value={formData.shippingAddress} onChange={(e) => setFormData({...formData, shippingAddress: e.target.value})} className="w-full bg-ink/50 border border-edge/50 text-ice px-3 py-2 text-sm rounded-none" placeholder="Enter shipping address" rows="3" />
              </div>

              <div className="border border-edge/50 rounded-none p-4 bg-ink/20">
                <h3 className="text-brand font-bold text-sm mb-3 uppercase tracking-wide">Products & Services</h3>
                <div className="space-y-2">
                  {formData.lineItems.map((item, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2">
                      <input type="text" value={item.description} onChange={(e) => { const n=[...formData.lineItems]; n[i].description=e.target.value; setFormData({...formData, lineItems:n}); }} className="col-span-5 bg-ink/50 border border-edge/50 text-ice px-2 py-1 text-sm rounded-none" placeholder="Description" />
                      <input type="number" value={item.quantity} onChange={(e) => { const n=[...formData.lineItems]; n[i].quantity=parseFloat(e.target.value)||0; n[i].lineTotal=n[i].quantity*n[i].unitPrice; setFormData({...formData, lineItems:n}); }} className="col-span-2 bg-ink/50 border border-edge/50 text-ice px-2 py-1 text-sm rounded-none" />
                      <input type="number" value={item.unitPrice} onChange={(e) => { const n=[...formData.lineItems]; n[i].unitPrice=parseFloat(e.target.value)||0; n[i].lineTotal=n[i].quantity*n[i].unitPrice; setFormData({...formData, lineItems:n}); }} className="col-span-2 bg-ink/50 border border-edge/50 text-ice px-2 py-1 text-sm rounded-none" />
                      <input type="number" value={item.lineTotal.toFixed(2)} className="col-span-2 bg-ink/50 border border-edge/50 text-ice px-2 py-1 text-sm rounded-none" readOnly />
                      <button onClick={() => setFormData({...formData, lineItems:formData.lineItems.filter((_,idx)=>idx!==i)})} className="col-span-1 text-red-400 text-xs">✕</button>
                    </div>
                  ))}
                  <button onClick={() => setFormData({...formData, lineItems:[...formData.lineItems,{description:'',quantity:1,unitPrice:0,lineTotal:0}]})} className="text-brand text-xs">+ Add</button>
                </div>
              </div>

              <div className="border border-edge/50 rounded-none p-4 bg-ink/20">
                <h3 className="text-brand font-bold text-sm mb-3">Subtotal: ${formData.lineItems.reduce((s,i)=>s+i.lineTotal,0).toFixed(2)}</h3>
              </div>

              <div className="border border-edge/50 rounded-none p-4 bg-ink/20">
                <h3 className="text-brand font-bold text-sm mb-3 uppercase tracking-wide">Fees & Shipping</h3>
                <div className="space-y-2">
                  {formData.fees.map((fee, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2">
                      <input type="text" value={fee.description} onChange={(e) => { const n=[...formData.fees]; n[i].description=e.target.value; setFormData({...formData, fees:n}); }} className="col-span-9 bg-ink/50 border border-edge/50 text-ice px-2 py-1 text-sm rounded-none" placeholder="Fee description" />
                      <input type="number" value={fee.amount} onChange={(e) => { const n=[...formData.fees]; n[i].amount=parseFloat(e.target.value)||0; setFormData({...formData, fees:n}); }} className="col-span-2 bg-ink/50 border border-edge/50 text-ice px-2 py-1 text-sm rounded-none" />
                      <button onClick={() => setFormData({...formData, fees:formData.fees.filter((_,idx)=>idx!==i)})} className="col-span-1 text-red-400 text-xs">✕</button>
                    </div>
                  ))}
                  <button onClick={() => setFormData({...formData, fees:[...formData.fees,{description:'',amount:0}]})} className="text-brand text-xs">+ Add Fee</button>
                </div>
              </div>

              <div className="rounded-none p-4 bg-brand/5">
                <h3 className="text-brand font-bold text-lg">Total: ${(formData.lineItems.reduce((s,i)=>s+i.lineTotal,0)+formData.fees.reduce((s,f)=>s+f.amount,0)).toFixed(2)}</h3>
              </div>

              <div className="space-y-4">
                {/* Payment Processing (VISA only) and Attempts - additive UI */}
                <div className="border border-edge/50 rounded-none p-4 bg-ink/20">
                  <VisaPaymentPanel
                    invoice={{ ...formData, invoice_id: formData.invoice_id || `INV-DRAFT` }}
                    onUpdateInvoice={(updated) => setFormData(prev => ({ ...prev, paymentAttempts: updated.paymentAttempts || prev.paymentAttempts }))}
                  />
                </div>

                <div className="border border-edge/50 rounded-none p-4 bg-ink/20">
                  <h3 className="text-brand font-bold text-sm mb-3 uppercase tracking-wide">Payment History</h3>
                  <div className="space-y-2">
                    {formData.payments.map((p, i) => (
                      <div key={i} className="grid grid-cols-12 gap-2">
                        <input type="date" value={p.date} onChange={(e) => { const n=[...formData.payments]; n[i].date=e.target.value; setFormData({...formData, payments:n}); }} className="col-span-3 bg-ink/50 border border-edge/50 text-ice px-2 py-1 text-sm rounded-none" />
                        <input type="text" value={p.source} onChange={(e) => { const n=[...formData.payments]; n[i].source=e.target.value; setFormData({...formData, payments:n}); }} className="col-span-3 bg-ink/50 border border-edge/50 text-ice px-2 py-1 text-sm rounded-none" placeholder="Source" />
                        <input type="text" value={p.description} onChange={(e) => { const n=[...formData.payments]; n[i].description=e.target.value; setFormData({...formData, payments:n}); }} className="col-span-4 bg-ink/50 border border-edge/50 text-ice px-2 py-1 text-sm rounded-none" placeholder="Description" />
                        <input type="number" value={p.amount} onChange={(e) => { const n=[...formData.payments]; n[i].amount=parseFloat(e.target.value)||0; setFormData({...formData, payments:n}); }} className="col-span-1 bg-ink/50 border border-edge/50 text-ice px-2 py-1 text-sm rounded-none" />
                        <button onClick={() => setFormData({...formData, payments:formData.payments.filter((_,idx)=>idx!==i)})} className="col-span-1 text-red-400 text-xs">✕</button>
                      </div>
                    ))}
                    <button onClick={() => setFormData({...formData, payments:[...formData.payments,{date:'',source:'',description:'',amount:0}]})} className="text-brand text-xs">+ Add Payment</button>
                  </div>
                  <div className="mt-3 text-sm">Total Payments: ${formData.payments.reduce((s,p)=>s+p.amount,0).toFixed(2)}</div>

                  {/* Display simulated paymentAttempts (read-only list) */}
                  <div className="mt-4">
                    <h4 className="text-ice font-medium text-sm mb-2">Simulated Payment Attempts</h4>
                    {formData.paymentAttempts && formData.paymentAttempts.length > 0 ? (
                      <div className="space-y-2">
                        {formData.paymentAttempts.map(a => (
                          <div key={a.attemptId} className="bg-ink/40 border border-edge/30 rounded-none p-3">
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="text-ice font-medium">{a.method.toUpperCase()} • ${a.amount.toFixed(2)}</div>
                                <div className="text-mist/70 text-xs">{new Date(a.createdAt).toLocaleString()}</div>
                                {a.failureReason && <div className="text-red-400 text-sm mt-1">{a.failureReason}</div>}
                                {a.receiptNumber && <div className="text-mist/70 text-sm mt-1">Receipt: {a.receiptNumber}</div>}
                              </div>
                              <div>
                                {a.status === 'failed' && (
                                  <button onClick={() => {
                                    // create a retry via VisaPaymentPanel by calling its onUpdate flow: mimic by appending a new pending attempt
                                    const pg = a.paymentGroupId || `pg-${Date.now()}`;
                                    const newAttempt = {
                                      attemptId: `att-${Date.now()}-${Math.random().toString(36).slice(2,6)}`,
                                      paymentGroupId: pg,
                                      invoiceId: formData.invoice_id || 'INV-DRAFT',
                                      method: 'visa',
                                      amount: a.amount,
                                      currency: a.currency || 'USD',
                                      status: 'pending',
                                      createdAt: new Date().toISOString()
                                    };
                                    setFormData(prev => ({ ...prev, paymentAttempts: [...(prev.paymentAttempts||[]), newAttempt] }));
                                    // resolve retry after 600ms
                                    setTimeout(() => {
                                      setFormData(prev => {
                                        const list = [...(prev.paymentAttempts||[])];
                                        const idx = list.findIndex(x => x.attemptId === newAttempt.attemptId);
                                        const resolved = { ...newAttempt, status: 'succeeded', receiptNumber: `RCT-${Date.now()}` };
                                        if (idx !== -1) list[idx] = resolved; else list.push(resolved);
                                        return { ...prev, paymentAttempts: list };
                                      });
                                    }, 600);
                                  }} className="bg-brand text-ink px-3 py-1 rounded-none text-sm">Retry</button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-mist/70 text-sm">No payment attempts</div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t border-edge/50 p-6 bg-ink/30 flex gap-3 justify-between">
              <label className="bg-brand/10 border border-brand/30 text-white hover:bg-brand/20 px-6 py-2 rounded-none text-sm font-medium cursor-pointer">
                📄 Import CSV
                <input type="file" accept=".csv" className="hidden" onChange={handleCSVUpload} />
              </label>
              <div className="flex gap-3">
                  <button onClick={() => setShowCSVModal(false)} className="bg-brand/10 border border-brand/30 text-white hover:bg-brand/20 px-6 py-2 rounded-none text-sm font-medium">Cancel</button>
                  <button onClick={() => {
                    // Pay Invoice: open Billing page with invoice context
                    const subtotal = formData.lineItems.reduce((s,i)=>s+(i.lineTotal||0),0);
                    const feesTotal = formData.fees.reduce((s,f)=>s+(f.amount||0),0);
                    const suggested = (subtotal + feesTotal).toFixed(2);
                    const id = formData.invoice_id || formData.invoiceId || `INV-DRAFT-${Date.now()}`;
                    const qs = new URLSearchParams({ invoiceId: id, amount: suggested }).toString();
                    setShowCSVModal(false);
                    navigate(`/sales/billing?${qs}`);
                  }} className="bg-blue-600 text-white px-6 py-2 rounded-none text-sm font-medium">Pay Invoice</button>
                  <button onClick={handleSaveInvoice} className="bg-brand/10 border border-brand/30 text-white hover:bg-brand/20 px-6 py-2 rounded-none text-sm font-medium">Save Invoice</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
